import { USER_TIERS, normalizeTier } from '../config/entitlements';

const ENTITLEMENTS_CACHE_KEY = 'vcs_entitlements_snapshot';
const ENTITLEMENTS_CACHE_TTL_MS = 5 * 60 * 1000;

 /* date_of_change: 2026-04-05
    rule_reference: Auth entitlement alignment - derive VCS premium access from Clerk publicMetadata.subscriptionStatus and subscriptionPlans so the simulator matches sixsmithgames.com subscription metadata semantics. */
 const ACTIVE_SUBSCRIPTION_STATUSES = new Set(['active']);
 const PREMIUM_SUBSCRIPTION_PLAN_IDS = new Set(['bundle', 'virtual-combat-simulator']);

const getNestedValue = (root, path) => path.reduce((value, key) => (value == null ? undefined : value[key]), root);

function safeGetLocalStorage() {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return null;
  }
  return window.localStorage;
}

export function persistResolvedEntitlements(snapshot) {
  const storage = safeGetLocalStorage();
  if (!storage || !snapshot || typeof snapshot !== 'object') {
    return;
  }

  const nextSnapshot = {
    userTier: normalizeTier(snapshot.userTier),
    limits: snapshot.limits || null,
    source: snapshot.source || 'unknown',
    fetchedAt: Date.now(),
  };

  storage.setItem(ENTITLEMENTS_CACHE_KEY, JSON.stringify(nextSnapshot));
}

export function clearResolvedEntitlements() {
  const storage = safeGetLocalStorage();
  if (!storage) {
    return;
  }
  storage.removeItem(ENTITLEMENTS_CACHE_KEY);
}

export function readResolvedEntitlements() {
  const storage = safeGetLocalStorage();
  if (!storage) {
    return null;
  }

  try {
    const raw = storage.getItem(ENTITLEMENTS_CACHE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    if (!parsed.fetchedAt || Date.now() - Number(parsed.fetchedAt) > ENTITLEMENTS_CACHE_TTL_MS) {
      storage.removeItem(ENTITLEMENTS_CACHE_KEY);
      return null;
    }
    return {
      ...parsed,
      userTier: normalizeTier(parsed.userTier),
    };
  } catch (_error) {
    storage.removeItem(ENTITLEMENTS_CACHE_KEY);
    return null;
  }
}

 function resolveTierFromSubscriptionMetadata(user) {
   const subscriptionStatus = String(getNestedValue(user, ['publicMetadata', 'subscriptionStatus']) || '').trim().toLowerCase();
   const rawPlans = getNestedValue(user, ['publicMetadata', 'subscriptionPlans']);
   const subscriptionPlans = Array.isArray(rawPlans)
     ? rawPlans.map((planId) => String(planId || '').trim().toLowerCase()).filter(Boolean)
     : [];

   if (!subscriptionStatus && subscriptionPlans.length === 0) {
     return null;
   }

   if (!ACTIVE_SUBSCRIPTION_STATUSES.has(subscriptionStatus)) {
     return USER_TIERS.FREE;
   }

   return subscriptionPlans.some((planId) => PREMIUM_SUBSCRIPTION_PLAN_IDS.has(planId))
     ? USER_TIERS.PREMIUM
     : USER_TIERS.FREE;
 }

 function resolveTierFromLegacyMetadata(user) {
   const candidates = [
     getNestedValue(user, ['publicMetadata', 'userTier']),
     getNestedValue(user, ['publicMetadata', 'tier']),
   ];

   const match = candidates.find((candidate) => candidate != null && String(candidate).trim().length > 0);
   return match == null ? null : normalizeTier(match);
 }

export function resolveTierFromUser(user) {
   const subscriptionTier = resolveTierFromSubscriptionMetadata(user);
   if (subscriptionTier) {
     return subscriptionTier;
   }

   const legacyTier = resolveTierFromLegacyMetadata(user);
   if (legacyTier) {
     return legacyTier;
   }

  return USER_TIERS.FREE;
}

export function resolveRuntimeTier() {
  if (typeof window === 'undefined') {
    return normalizeTier(process.env.REACT_APP_DEFAULT_USER_TIER || USER_TIERS.FREE);
  }

  const clerkUser = window.Clerk?.user || window.Clerk?.client?.user || null;
  const userTier = resolveTierFromUser(clerkUser);
  return userTier || normalizeTier(process.env.REACT_APP_DEFAULT_USER_TIER || USER_TIERS.FREE);
}
